import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { ComponentProps, useEffect, useRef, useState } from 'react'
import { useMissingCustomerAddressToast } from 'common/hooks'
import { useCustomer } from '../../../hooks/useCustomer'
import { CustomerContactsContent } from './CustomerContactsContent'
import { match } from 'ts-pattern'
import { ValidationItem } from 'common/components/SteppedModal/useSteppedModal'
import { getMissingTaxRateMessage } from 'SettingsV2/components/tax-rates/taxRates.utils'
import { useQuery } from '@sequencehq/api/utils'
import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider.ts'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { BillingScheduleContextData } from 'Cube/domain'
import { CubeStatus } from 'Cube/domain/cube.domain.types'
import { createBaseBillingSchedulePhasesApiData } from 'Cube/communication/external/billingSchedule.api.v1/adapters/billingSchedule.adapters.phases.out'
import {
  v1ApiNewBillingSchedule,
  v1ApiUpdateBillingSchedule
} from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/entitySaving/useSaveBillingSchedule'
import {
  parseValidationResult,
  processValidationErrors
} from 'Cube/utils/validation'
import { CountriesAlpha2 } from '@sequencehq/api/commonEnums'
import { useCreateTaxRate } from 'SettingsV2/components/tax-rates/hooks/useCreateTaxRate'
import { TaxRate } from 'SettingsV2/components/tax-rates/taxRates.types'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'
import { ControlledTaxRatesModal } from 'SettingsV2/components/tax-rates/modals/TaxRatesModal'

const baseContactValidation = {
  stepName: 'Primary billing contact',
  stepKey: 'CONTACTS',
  component: CustomerContactsContent
}

const baseTaxRatesValidation = {
  stepKey: 'TAX_RATES',
  stepName: 'Tax rates',
  component: ControlledTaxRatesModal
}

const baseValidation = {
  contacts: baseContactValidation,
  taxRates: baseTaxRatesValidation
}

// Maps a validData key to the step key of the step that should follow it
const getNextStep = (key: string, allKeys: string[]) =>
  match(key)
    .with('contacts', () => 'CONFIRM')
    .with('taxRates', () =>
      allKeys.includes('contacts') ? 'CONTACTS' : 'CONFIRM'
    )
    .otherwise(() => 'CONFIRM')

const isUpdate = (
  billingScheduleData: v1ApiUpdateBillingSchedule | v1ApiNewBillingSchedule
): billingScheduleData is v1ApiUpdateBillingSchedule =>
  !('customerId' in billingScheduleData)

const buildValidationRequest = (
  billingScheduleQueries: BillingScheduleContextData['queries']
) => {
  const baseValidationData = createBaseBillingSchedulePhasesApiData(
    billingScheduleQueries
  ).billingSchedule

  const requestData: Omit<
    DashboardApi20240730.PostBillingScheduleValidateTaxes.PostBillingScheduleValidateTaxesBody,
    'isDraft'
  > = isUpdate(baseValidationData)
    ? {
        ...baseValidationData,
        customerId: billingScheduleQueries.rawData.data.common.customerId
      }
    : baseValidationData

  return requestData
}

export const useSteppedConfirmStartModal = (props: {
  onClose: () => void
  billingScheduleQueries: BillingScheduleContextData['queries']
}) => {
  const { billingScheduleQueries, onClose } = props
  const [validation, setValidation] = useState<ValidationItem[] | null>(null)
  const {
    isLoading: isCustomerLoading,
    isFetching: isCustomerFetching,
    customer
  } = useCustomer()
  const { addresses, id: customerId } = customer ?? {}
  const address = getBillingAddress(addresses)
  const showMissingAddressToast = useMissingCustomerAddressToast()
  const reportedMissingAddress = useRef(false)

  /**
   * Every validation step below is keyed on the customer's country, and the
   * backend rejects the tax check outright without one, so there is nothing
   * this modal can usefully show. Say why and close, rather than sitting on a
   * spinner that never resolves.
   */
  useEffect(() => {
    // Wait for any in-flight refetch: a cached customer whose address was
    // added elsewhere still reads as address-less until the server answers,
    // and closing on that would be wrong.
    if (
      isCustomerLoading ||
      isCustomerFetching ||
      !customer ||
      address ||
      reportedMissingAddress.current
    ) {
      return
    }

    reportedMissingAddress.current = true
    showMissingAddressToast('starting their schedule')
    onClose()
  }, [
    isCustomerLoading,
    isCustomerFetching,
    customer,
    address,
    showMissingAddressToast,
    onClose
  ])
  const { data: taxRatesData, isLoading: taxRatesLoading } = useQuery(
    dashboardv99990101Client.getTaxRates
  )
  const { isAvalara } = useTaxIntegrationProvider()

  const validationRequestData = buildValidationRequest(billingScheduleQueries)
  const validationResponse = useQuery(
    dashboard20240730Client.postBillingScheduleValidateTaxes,
    {
      body: {
        ...validationRequestData,
        isDraft:
          billingScheduleQueries.rawData.data.common.status ===
          CubeStatus.ScheduleDraft
      }
    },
    {
      retry: false
    }
  )

  const { validating, hasValidationError, validationErrors } =
    parseValidationResult(validationResponse)

  useEffect(() => {
    if (
      taxRatesLoading ||
      validationResponse.isLoading === true ||
      validating ||
      validation !== null ||
      !customerId ||
      !address
    ) {
      return
    }

    const invalidKeys = Object.entries(validData)
      .filter(([, isValid]) => !isValid)
      .map(([validationKey]) => validationKey)

    setValidation(
      Object.entries(validData).reduce((acc, [key, value]) => {
        if (value === true) {
          return acc
        }

        const componentProps = match(key)
          .with(
            'contacts',
            (): ComponentProps<typeof CustomerContactsContent> => ({
              customerId,
              onClose: onClose,
              onSubmit: () => Promise.resolve()
            })
          )
          .with(
            'taxRates',
            (): ComponentProps<typeof ControlledTaxRatesModal> => ({
              'data-testid': 'addTaxRates',
              countriesWithTaxRates: {
                [address.country]: taxRates.filter(
                  taxRate => taxRate.country === address.country
                )
              } as Record<CountriesAlpha2, TaxRate[]>,
              preselectedCountry: address.country,
              preselectedState: 'state' in address ? address.state : undefined,
              onSubmit: async values => {
                await createTaxRate(values)
              },
              onClose: onClose,
              options: {
                closeOnCancel: false,
                closeOnSubmit: false,
                submitText: validData.contacts
                  ? 'Save tax rates'
                  : 'Save and continue'
              },
              filterTaxCategories: taxCategories =>
                taxCategories.filter(({ id }) =>
                  missingRatesForCategories.includes(id)
                )
            })
          )
          .otherwise(() => ({}))

        const validationMessage = match(key as keyof typeof validData)
          .with('contacts', () => 'Primary billing contact')
          .with('taxRates', () =>
            getMissingTaxRateMessage({
              country: address.country,
              state: 'state' in address ? address.state : undefined,
              taxRates
            })
          )
          .exhaustive()

        return [
          ...acc,
          {
            ...baseValidation[key as keyof typeof baseValidation],
            message: validationMessage,
            nextStep: getNextStep(key, invalidKeys),
            componentProps
          } as ValidationItem
        ]
      }, [] as ValidationItem[])
    )
  }, [
    taxRatesLoading,
    address,
    customerId,
    onClose,
    validating,
    validation,
    validationResponse.isLoading
  ])

  const createTaxRate = useCreateTaxRate({ invalidateQueries: false })

  const taxRates = taxRatesData?.items ?? []
  const missingRatesForCategories = processValidationErrors(validationErrors)
  const hasTaxCategoryError = missingRatesForCategories.length > 0

  const taxRatesValid =
    !hasValidationError || (hasValidationError && !hasTaxCategoryError)

  const validData = {
    taxRates: taxRatesValid,
    contacts: Boolean(customer?.contacts?.length)
  }

  const showAvalaraBlocker = isAvalara && validData.taxRates === false

  return {
    loading: taxRatesLoading || isCustomerLoading,
    validation,
    showAvalaraBlocker
  }
}
